import { Component, Input } from '@angular/core';
import { Store } from '@ngrx/store';
import { map, Observable } from 'rxjs';

import {
  selectHoodTypeKeys,
  selectProjectEntityNotes,
  selectTemporaryHoodlineFeedbackMessages
} from '../../store/project-data/project-data.selectors';
import { selectUIProductSidebarOpened } from '../../store/product-view/product-view.selectors';
import { ProductTreeTraverseService } from 'src/app/core/services/productTreeTraverse.service';
import type { EntityNote } from '../../../core/models/entity-note';
import type { TreeEntity } from '../../../core/models/entity.model';
import { ProductViewActions } from '../../store/product-view/product-view.actions';
import { ActivatedRoute } from '@angular/router';
import { PropertyType } from 'src/app/core/models/property-type.model';
import { TranslateService } from '@ngx-translate/core';
import { selectPropertyTypes } from 'src/app/core/store/entity-types/entity-types.selectors';
import { EntityTypeKey } from 'src/app/shared/constants/entity-type-keys';

/**
 * Property name where the entity error name is found
 */
const errorNamePropertyKey = 'UIErrorName';

@Component({
  selector: 'app-hoodline-messages',
  templateUrl: './hoodline-messages.component.html',
  styleUrls: ['./hoodline-messages.component.scss'],
  standalone: false
})
export class HoodLineMessagesComponent {
  @Input() messageFilter: (message: TreeEntity) => boolean;
  @Input() overlapMessages: TreeEntity[];
  @Input() hoodlines: TreeEntity[];

  feedbackMessages$: Observable<TreeEntity[]>;
  notes$: Observable<EntityNote[]>;
  productSidebarOpened$: Observable<boolean>;
  hoodTypeKeys$: Observable<string[]>;
  propertyTypes$: Observable<Record<string, PropertyType>>;

  selectedId = '';

  constructor(
    private store: Store,
    private route: ActivatedRoute,
    private translate: TranslateService
  ) {
    this.selectedId = this.route.snapshot.queryParamMap.get('selectedId');

    this.feedbackMessages$ = this.store.select(selectTemporaryHoodlineFeedbackMessages).pipe(
      map(message => {
        const currentMessages = message.filter(msg => this.isSelectedItemMessage(msg));

        let messages = [];

        if (this.messageFilter) {
          messages = currentMessages.filter(this.messageFilter);
        } else {
          messages = currentMessages;
        }

        const nonDuplicateMessages = messages.filter(
          (obj, index, self) => index === self.findIndex(t => this.getMessageSignature(t) === this.getMessageSignature(obj))
        );

        return nonDuplicateMessages;
      })
    );

    this.notes$ = this.store.select(selectProjectEntityNotes);
    this.productSidebarOpened$ = this.store.select(selectUIProductSidebarOpened);
    this.hoodTypeKeys$ = this.store.select(selectHoodTypeKeys);
    this.propertyTypes$ = this.store.select(selectPropertyTypes);
  }

  /**
   * Creates a "shortcode" to be displayed with an error message. This allows the target entity to be identified by the user.
   * For example: Hood 1, Module 2, Supply device would result in "H1M2S".
   * @param entity
   * @param hoodTypeKeys
   */
  getMessageTargetCode(entity: TreeEntity, hoodTypeKeys: string[]) {
    // Get the listing of all parents up to the hood
    const parentGraph = ProductTreeTraverseService.findParentGraphUpToHood(entity, hoodTypeKeys);

    const entityTags = parentGraph
      .map(entity => {
        const errorNameProperty = entity.Properties[errorNamePropertyKey];
        if (!errorNameProperty) {
          return null;
        }
        return errorNameProperty.Value;
      })
      .filter(x => !!x);

    // Filter out empty values and nulls, reverse the list (child->parent to parent->child) order, then create a string out of it
    return entityTags.reverse().join('');
  }

  select(message: TreeEntity) {
    if (
      message.Parent.EntityTypeKey === EntityTypeKey.GastroFlowHoodLine ||
      message.Parent.EntityTypeKey === EntityTypeKey.GastroFlowHoodConnectionLine
    ) {
      this.store.dispatch(ProductViewActions.open_hoodline({ id: message.ParentId, closeOthers: true }));
    } else if (message.Parent.EntityTypeKey === EntityTypeKey.GastroFlowOutsidePanel) {
      const module = message.Parent?.Parent;

      if (module) {
        const lineNumber = module.Properties['LineNumber'].Value as number;

        const hoodline = this.hoodlines.find(x => (x.Properties['Number']?.Value as number) === lineNumber);

        if (hoodline) {
          this.store.dispatch(ProductViewActions.open_hoodline({ id: hoodline.Id, closeOthers: true }));
        }
      }
    }
  }

  getMessageSignature = (message: TreeEntity) => {
    const text = message.Properties['Text'].Value ?? message.Properties['TextId'].Value;

    return `${message.ParentId}-${text}`;
  };

  getPropertyName(message: TreeEntity, types: Record<string, PropertyType>) {
    const propertyKey = message.Properties['PropertyKey'].Value as string;

    if (propertyKey) {
      const nameId = types[propertyKey]?.NameId ?? '';

      return this.translate.instant(nameId);
    } else {
      return this.translate.instant(`${message.Parent.EntityTypeKey}.${message.Parent.EntityTypeKey}`);
    }
  }

  getAllMessages(messages: TreeEntity[], overlapMessages: TreeEntity[]): TreeEntity[] {
    const result = [...messages, ...overlapMessages];

    return result;
  }

  isSelectedItemMessage(entity: TreeEntity): boolean {
    if (entity.ParentId === this.selectedId) {
      return true;
    } else if (entity.EntityTypeKey === EntityTypeKey.GastroFlowHood) {
      return false;
    } else if (!entity) {
      return false;
    } else {
      return this.isSelectedItemMessage(entity.Parent);
    }
  }
}
